import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getClusterResources } from '@/services/permissionsService';
import {
  getGeoPolice,
  getLedgerMeta,
  getPartition,
  getPartitionBundle,
  getPolices,
  getProPolices,
  getStorages,
  getTopicDetailOverView,
  getTopicOverview,
  keyHashRangeMatch,
} from '@/services/topicService';

const initialState = {
  overview: {},
  partition: {},
  partitionBundle: {},
  polices: {},
  proPolices: {},
  policesFetching: false,
  partitionBundleFetching: false,
  tenantOptions: [],
  detailOverview: {},
  loading: false,
  storage: {},
  ledgerMeta: {},
  metaFetching: false,
  keyHash: null,
  keyHashSearching: false,
  fetchingGeoPolice: false,
  geoPolice: {},
};

export const fetchTopicOverview = createAsyncThunk(
  'topic/fetchNamespaceOverview',
  async (clusterName) => {
    const response = await getTopicOverview(clusterName);
    return response.data;
  },
);

// 单个topic 总览数据
export const fetchTopicDetailOverview = createAsyncThunk(
  'topic/fetchTopicDetailOverview',
  async (params) => {
    const response = await getTopicDetailOverView(params);
    return response.data;
  },
);

export const fetchTenantOptions = createAsyncThunk('topic/fetchTenantOptions', async (params) => {
  const response = await getClusterResources(params);
  return response.data;
});

// 分区信息
export const fetchPartition = createAsyncThunk('topic/fetchPartition', async (params) => {
  const response = await getPartition(params);
  return response.data;
});

export const fetchPartitionBundle = createAsyncThunk(
  'topic/fetchPartitionBundle',
  async (params) => {
    const response = await getPartitionBundle(params);
    return response.data;
  },
);

export const fetchPolices = createAsyncThunk('topic/fetchPolices', async (params) => {
  const response = await getPolices(params);
  return response.data;
});

export const fetchProPolices = createAsyncThunk('topic/fetchProPolices', async (params) => {
  const response = await getProPolices(params);
  return response.data;
});
export const fetchStorages = createAsyncThunk('topic/fetchStorages', async (params) => {
  const response = await getStorages(params);
  return response.data;
});
export const fetchLedgerMeta = createAsyncThunk('topic/fetchLedgerMeta', async (params) => {
  const response = await getLedgerMeta(params);
  return response.data;
});

export const fetchKeyHashRangeMatch = createAsyncThunk(
  'topic/fetchKeyHashRangeMatch',
  async (params) => {
    const response = await keyHashRangeMatch(params);
    return response.data;
  },
);

export const fetchGeoPolice = createAsyncThunk('topic/fetchGeoPolice', async (params) => {
  const response = await getGeoPolice(params);
  return response.data;
});

export const topicSlice = createSlice({
  name: 'topic',
  initialState,
  reducers: {
    resetKeyHash: (state) => {
      state.keyHash = null;
    },
    resetDetail: (state) => {
      state.detailOverview = {};
      state.partition = {};
      state.partitionBundle = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTopicOverview.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTopicOverview.fulfilled, (state, action) => {
        state.overview = action.payload;
        state.loading = false;
      })
      .addCase(fetchTopicOverview.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchTenantOptions.fulfilled, (state, action) => {
        state.tenantOptions = action.payload.map((item) => ({
          label: item,
          value: item,
        }));
      })
      .addCase(fetchTopicDetailOverview.pending, (state) => {
        state.detailOverview = {};
        state.loading = true;
      })
      .addCase(fetchTopicDetailOverview.fulfilled, (state, action) => {
        state.detailOverview = action.payload || {};
        state.loading = false;
      })
      .addCase(fetchTopicDetailOverview.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchPartition.pending, (state) => {
        state.partition = {};
      })
      .addCase(fetchPartition.fulfilled, (state, action) => {
        const data = action.payload;
        state.partition = {
          ...data,
          topicPartitionMetricsList: data.topicPartitionMetricsList?.sort(
            (a, b) => a.partition - b.partition,
          ),
          bundle: {
            bundleRange: data.bundle.bundleRange,
            serviceUrl: data.bundle.serviceUrl,
          },
          originBundle: data.bundle,
        };
      })
      .addCase(fetchPartitionBundle.pending, (state) => {
        state.partitionBundle = {};
        state.partitionBundleFetching = true;
      })
      .addCase(fetchPartitionBundle.fulfilled, (state, action) => {
        const data = action.payload;
        state.partitionBundle = {
          ...data,
          bundle: {
            bundleRange: data.bundleRange,
            serviceUrl: data.serviceUrl,
          },
        };
        state.partitionBundleFetching = false;
      })
      .addCase(fetchPartitionBundle.rejected, (state) => {
        state.partitionBundleFetching = false;
      })
      .addCase(fetchPolices.pending, (state) => {
        state.policesFetching = true;
        state.polices = {};
      })
      .addCase(fetchPolices.fulfilled, (state, action) => {
        state.polices = action.payload;
        state.policesFetching = false;
      })
      .addCase(fetchPolices.rejected, (state) => {
        state.policesFetching = false;
      })
      .addCase(fetchProPolices.pending, (state) => {
        state.policesFetching = true;
        state.proPolices = {};
      })
      .addCase(fetchProPolices.fulfilled, (state, action) => {
        const data = action.payload;
        state.proPolices = {
          ...data,
          ensemble: data.bookkeeperEnsemble,
          writeQuorum: data.bookkeeperWriteQuorum,
          ackQuorum: data.bookkeeperAckQuorum,
        };
        state.policesFetching = false;
      })
      .addCase(fetchProPolices.rejected, (state) => {
        state.policesFetching = false;
      })
      .addCase(fetchStorages.pending, (state) => {
        state.storage = {};
      })
      .addCase(fetchStorages.fulfilled, (state, action) => {
        state.storage = action.payload;
      })
      .addCase(fetchLedgerMeta.pending, (state) => {
        state.metaFetching = true;
        state.ledgerMeta = {};
      })
      .addCase(fetchLedgerMeta.fulfilled, (state, action) => {
        state.ledgerMeta = action.payload;
        state.metaFetching = false;
      })
      .addCase(fetchLedgerMeta.rejected, (state) => {
        state.metaFetching = false;
      })
      .addCase(fetchKeyHashRangeMatch.pending, (state) => {
        state.keyHashSearching = true;
        state.keyHash = null;
      })
      .addCase(fetchKeyHashRangeMatch.fulfilled, (state, action) => {
        state.keyHashSearching = false;
        state.keyHash = action.payload;
      })
      .addCase(fetchKeyHashRangeMatch.rejected, (state) => {
        state.keyHashSearching = false;
      })

      .addCase(fetchGeoPolice.pending, (state) => {
        state.fetchingGeoPolice = true;
      })
      .addCase(fetchGeoPolice.fulfilled, (state, action) => {
        state.fetchingGeoPolice = false;
        state.geoPolice = action.payload;
      })
      .addCase(fetchGeoPolice.rejected, (state) => {
        state.fetchingGeoPolice = false;
      });
  },
});

export const { resetKeyHash, resetDetail } = topicSlice.actions;
export default topicSlice.reducer;
